import { Inject, Injectable, InjectionToken, OnDestroy, Optional, Provider } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, Subject } from 'rxjs';

import { DataUtil, hasValue, isArrayEqual } from '@celum/core/util';

import { AbstractList } from '../components/abstract-list';
import { SelectionBehavior } from '../model/list-selection-handler.model';

export const LIST_SELECTION_CONFIG = new InjectionToken<ListSelectionConfiguration>('LIST_SELECTION_CONFIG');

export type ListSelectionConfiguration = {
  /** Configure whether the selection is updated if items change. By default, the selection is updated. */
  updateSelectionOnItemsChange?: boolean;
  selectionBehaviour?: SelectionBehavior;
  selectionLimit?: number;
  /**
   * Shift selection mode determines how Shift+click modifies selection:
   * - 'replace' (default): selects the contiguous range between anchor and clicked item, replacing previous selection
   * - 'modify'           : uses the anchor's selected state to either add or remove the range to the previous selection
   */
  shiftSelectionMode?: 'replace' | 'modify';
};

type SelectionState<E> = {
  // shift selection is determined by this item, which is determined by the last click or ctrl click
  selectionAnchorItem: E;
  // whether the anchor item was selected at the time it became the anchor. Becomes relevant if shiftSelectionMode is `modify`
  selectionAnchorSelected: boolean;
  selection: E[];
  items: E[];
  selectionBehavior: SelectionBehavior;
  deselectOnClick: boolean;
  selectionLimit?: number;
};

type ListSelectionOptions = {
  silent?: boolean;
  skipAnchor?: boolean;
};

@Injectable()
export class ListSelectionHandler<E> extends ComponentStore<SelectionState<E>> implements OnDestroy {
  public selectionChanged$: Observable<E[]>;
  public selection$: Observable<E[]> = this.select(state => state.selection);
  public selectionCleared$: Observable<void>;
  public updateSelectionOnItemsChange: boolean;

  private connectedList: AbstractList<E>;
  private selectionChangedSubj = new Subject<E[]>();
  private selectionClearedSubj = new Subject<void>();
  private shiftSelectionMode: 'replace' | 'modify' = 'replace';

  constructor(@Optional() @Inject(LIST_SELECTION_CONFIG) private config?: ListSelectionConfiguration) {
    super({
      selection: [],
      items: [],
      selectionAnchorItem: null,
      selectionAnchorSelected: false,
      selectionBehavior: config?.selectionBehaviour || SelectionBehavior.SINGLE,
      deselectOnClick: true
    });
    this.selectionChanged$ = this.selectionChangedSubj.asObservable();
    this.selectionCleared$ = this.selectionClearedSubj.asObservable();

    this.updateSelectionOnItemsChange = this.config?.updateSelectionOnItemsChange ?? true;
    this.shiftSelectionMode = this.config?.shiftSelectionMode ?? 'replace';
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.selectionChangedSubj.complete();
    this.selectionClearedSubj.complete();
  }

  public setCompareFn(compare: (a: E, b: E) => boolean): void {
    this.compareFn = compare;
  }

  public setConnectedList(connectedList: AbstractList<E>): void {
    this.connectedList = connectedList;
  }

  public setSelectionBehavior(selectionBehavior: SelectionBehavior): void {
    this.patchState({ selectionBehavior });
  }

  public setSelectionLimit(selectionLimit: number): void {
    this.patchState({ selectionLimit });
  }

  public setItems(items: E[]): void {
    this.patchState({ items });

    if (!this.updateSelectionOnItemsChange) {
      return;
    }

    const { selection } = this.get();

    if (!DataUtil.isEmpty(items) && !DataUtil.isEmpty(selection)) {
      const newSelection = selection.filter(selectedItem => this.isItemPartOf(selectedItem, items));

      const hasChanges = !isArrayEqual(selection, newSelection, { customEqualityCheck: this.compareFn });

      if (!hasChanges) {
        return;
      }

      this.patchState({ selection: newSelection });
      this.selectionChangedSubj.next([...newSelection]);
    } else if (DataUtil.isEmpty(items) && !DataUtil.isEmpty(selection)) {
      this.patchState({ selection: [] });
      this.selectionChangedSubj.next([]);
    }
  }

  public isInSelection = (item: E) => this.isItemPartOf(item, this.get().selection);

  public isDisabled = (item: E) => {
    const { selectionBehavior, selectionLimit, selection } = this.get();

    if (selectionBehavior === SelectionBehavior.MULTI_TOGGLE_LIMITED) {
      return selection.length >= selectionLimit && !this.isInSelection(item);
    } else {
      return false;
    }
  };

  /** Try to select items by id. Only works if the passed `idKey` actually exists on the items. Otherwise nothing will happen. */
  public selectById(ids: string[], scrollIntoView: boolean, silent: boolean, idKey = 'id'): boolean {
    const newSelection: E[] = [];
    let somethingSelected = false;

    ids.forEach(id => {
      const item = this.get().items.find(existingItem => (existingItem as any)[idKey] === id);

      if (item) {
        newSelection.push(item);
        somethingSelected = true;
      }
    });

    this.patchState({ selection: newSelection });

    if (!silent) {
      this.selectionChangedSubj.next([...newSelection]);
    }

    if (somethingSelected && scrollIntoView) {
      this.connectedList.scrollIntoView(ids);
    }

    return somethingSelected;
  }

  public clearSelection(silent = false): void {
    this.patchState({ selection: [], selectionAnchorItem: null });

    if (!silent) {
      this.selectionChangedSubj.next([]);
    }

    this.selectionClearedSubj.next();
  }

  public selectAll(silent = false): void {
    this.setSelection(this.capToLimit(this.get().items), { silent });
  }

  public setSelection(newSelection: E[], options?: ListSelectionOptions): void {
    const optionsWithDefaults = { silent: false, skipAnchor: false, ...options };
    if (!newSelection) {
      return;
    }

    const currentSelection = this.get().selection;

    if (isArrayEqual(currentSelection, newSelection, { customEqualityCheck: this.compareFn })) {
      return;
    }

    this.patchState({ selection: newSelection });

    const selectedAnchorItem = this.get().selectionAnchorItem;
    if (
      !optionsWithDefaults.skipAnchor &&
      (!hasValue(selectedAnchorItem) || !newSelection.find(item => this.compareFn(selectedAnchorItem, item))) &&
      newSelection.length
    ) {
      this.patchState({ selectionAnchorItem: newSelection[0] });
    }

    if (!optionsWithDefaults.silent) {
      this.selectionChangedSubj.next([...newSelection]);
    }
  }

  public getCurrentSelection(): E[] {
    return this.get().selection;
  }

  public getSelectionBehavior(): SelectionBehavior {
    return this.get().selectionBehavior;
  }

  public isActiveMultiSelection(): boolean {
    return this.get().selection.length > 1;
  }

  public handleItemClicked(event: MouseEvent, item: E): void {
    const { selection, selectionBehavior } = this.get();

    if (this.isDisabled(item)) {
      return;
    }

    if (selectionBehavior === SelectionBehavior.SINGLE) {
      !this.isInSelection(item) && this.setSelection([item]);
    } else if (selectionBehavior === SelectionBehavior.MULTI_TOGGLE || selectionBehavior === SelectionBehavior.MULTI_TOGGLE_LIMITED) {
      event.shiftKey ? this.doShiftSelection(item) : this.toggleSelection(item);
    } else {
      if (ListSelectionHandler.isMultiSelectionChangeForSingleItem(selection, event)) {
        this.toggleSelection(item);
      } else if (ListSelectionHandler.isMultiSelectionChangeWithShift(selection, event)) {
        this.doShiftSelection(item);
      } else if (selection.length > 1 || !this.isInSelection(item)) {
        this.setSelection([item]);
      }
    }
  }

  private compareFn: (a: E, b: E) => boolean = (a, b) => a === b;

  private toggleSelection(item: E): void {
    const selection = this.get().selection;

    let matchedItem: E;

    if (!DataUtil.isEmpty(selection)) {
      matchedItem = selection.find(selected => this.compareFn(selected, item));
    }

    if (!matchedItem) {
      // item not yet in the list, add it
      this.patchState({ selection: [...(selection ?? []), item] });
    } else {
      // item already in the list, remove it
      this.patchState({ selection: selection.filter(selectedItem => selectedItem !== matchedItem) });
    }

    this.selectionChangedSubj.next([...this.get().selection]);

    this.patchState({ selectionAnchorItem: item, selectionAnchorSelected: !matchedItem });
  }

  private doShiftSelection(newItem: E): void {
    const { selectionAnchorItem, items } = this.get();

    if (!selectionAnchorItem) {
      // set anchor and make newItem selected
      if (!this.isInSelection(newItem)) {
        const selection = [...this.get().selection, newItem];
        this.patchState({ selection });
        this.selectionChangedSubj.next(selection);
      }
      this.patchState({ selectionAnchorItem: newItem, selectionAnchorSelected: true });
      return;
    }

    const anchorIndex = items.findIndex(item => this.compareFn(item, selectionAnchorItem));
    const itemIndex = items.findIndex(item => this.compareFn(item, newItem));

    if (this.shiftSelectionMode === 'replace') {
      this.handleReplaceShiftSelection(anchorIndex, itemIndex);
    }

    if (this.shiftSelectionMode === 'modify') {
      this.handleModifyShiftSelection(anchorIndex, itemIndex);
    }
  }

  private handleReplaceShiftSelection(anchorIndex: number, itemIndex: number): void {
    // replace mode drops the previous selection, so nothing of it has to be kept
    this.patchState({ selection: this.capToLimit(this.getRange(anchorIndex, itemIndex), []) });
    this.selectionChangedSubj.next([...this.get().selection]);
  }

  private handleModifyShiftSelection(anchorIndex: number, itemIndex: number): void {
    const { selection, selectionAnchorSelected } = this.get();
    const range = this.getRange(anchorIndex, itemIndex);

    let newSelection: E[];
    if (selectionAnchorSelected) {
      // anchor was selected -> shift should add/select the whole range (as far as the selection limit allows, starting at the anchor)
      newSelection = this.capToLimit([...selection, ...range.filter(rangeItem => !this.isItemPartOf(rangeItem, selection))]);
    } else {
      // anchor was not selected -> shift should remove/deselect the whole range
      newSelection = selection.filter(item => !this.isItemPartOf(item, range));
    }

    this.patchState({ selection: newSelection });
    this.selectionChangedSubj.next([...newSelection]);
  }

  private getRange(anchorIndex: number, itemIndex: number): E[] {
    return this.get().items.slice(Math.min(anchorIndex, itemIndex), Math.max(anchorIndex, itemIndex) + 1);
  }

  /**
   * In MULTI_TOGGLE_LIMITED mode, drops items from `newSelection` that exceed the selection limit.
   * Items contained in `keep` always stay, the remaining slots go to the new items closest to the selection anchor.
   */
  private capToLimit(newSelection: E[], keep = this.get().selection): E[] {
    const { selectionBehavior, selectionLimit, items, selectionAnchorItem } = this.get();

    if (selectionBehavior !== SelectionBehavior.MULTI_TOGGLE_LIMITED || newSelection.length <= selectionLimit) {
      return newSelection;
    }

    const anchorIndex = items.findIndex(item => this.compareFn(item, selectionAnchorItem));

    // especially for shift-selection, add items that are closer to the anchor in favor over items that are farther away
    const distanceToAnchor = (item: E) => Math.abs(items.indexOf(item) - anchorIndex);
    const additions = newSelection.filter(item => !this.isItemPartOf(item, keep)).sort((a, b) => distanceToAnchor(a) - distanceToAnchor(b));
    const allowedAdditions = additions.slice(0, Math.max(0, selectionLimit - (newSelection.length - additions.length)));

    return newSelection.filter(item => !additions.includes(item) || allowedAdditions.includes(item));
  }

  private isItemPartOf(itemToFind: E, items: E[]): boolean {
    return items.some(item => this.compareFn(item, itemToFind));
  }

  public static withConfiguration(config: ListSelectionConfiguration): Provider[] {
    return [
      {
        provide: LIST_SELECTION_CONFIG,
        useValue: config
      },
      ListSelectionHandler
    ];
  }

  private static isActiveMultiSelection<E>(selection: E[]): boolean {
    return selection.length > 0;
  }

  private static isMultiSelectionChangeWithShift<E>(selection: E[], event: MouseEvent): boolean {
    return this.isActiveMultiSelection(selection) && event.shiftKey;
  }

  private static isMultiSelectionChangeForSingleItem<E>(selection: E[], event: MouseEvent): boolean {
    return this.isActiveMultiSelection(selection) && (event.ctrlKey || event.metaKey);
  }
}
